"use client";
import React, { useEffect, useRef, useState } from 'react';
import Portal from './Portal';

type Provider = {
  _id?: string;
  name: string;
  aliases?: string[];
  website?: string;
  rank?: number;
  reportsCount?: number;
};

export default function ProviderSelect({ value: _value, onChange }:{ value?: Provider | null; onChange: (p: Provider | null) => void }){
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [rect, setRect] = useState<{left:number; top:number; width:number; bottom:number} | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Index of the keyboard-highlighted option, -1 when none.
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(()=>{
    // if a value is provided (selected provider), show its name in the input
    if (_value && _value.name) setQ(_value.name);
    let canceled = false;
    async function fetchList(){
      setLoading(true);
      try{
        const url = q ? `/api/providers?q=${encodeURIComponent(q)}` : '/api/providers';
        const r = await fetch(url);
        const json = await r.json();
        if (!canceled) setOpts(json || []);
      }catch(err){
        console.error(err);
      }finally{ setLoading(false); }
    }
    const t = setTimeout(fetchList, 200);
    return ()=>{ canceled = true; clearTimeout(t); };
  }, [q, _value]);

  useEffect(()=>{
    function updateRect(){
      try{
        const el = inputRef.current;
        if (!el) { setRect(null); return; }
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.top, width: r.width, bottom: r.bottom });
      }catch{ setRect(null); }
    }
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return ()=>{ window.removeEventListener('resize', updateRect); window.removeEventListener('scroll', updateRect, true); };
  }, []);

  // close dropdown when clicking/tapping outside input or dropdown (so buttons on the page remain clickable)
  useEffect(()=>{
    function onDocPointerDown(e: PointerEvent){
      try{
        const target = e.target as Node | null;
        if (!target) return;
        if (inputRef.current && inputRef.current.contains(target)) return;
        if (dropdownRef.current && dropdownRef.current.contains(target)) return;
        // clicked outside -> close
        setFocused(false);
      }catch{ /* ignore */ }
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    return ()=> document.removeEventListener('pointerdown', onDocPointerDown);
  }, []);

  // listen for global close event so external buttons can force-close the dropdown
  useEffect(()=>{
    function onCloseEvent(){ setFocused(false); }
    document.addEventListener('close-dropdowns', onCloseEvent as EventListener);
    return ()=> document.removeEventListener('close-dropdowns', onCloseEvent as EventListener);
  }, []);

  useEffect(()=>{
    if (activeIndex < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelectorAll('[role="option"]')[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // A fresh query invalidates the highlight.
  useEffect(()=>{ setActiveIndex(-1); }, [q]);

  const hasSelection = !!_value && !!_value.name;

  return (
    <div className="w-full relative">
      <input
        ref={inputRef}
        aria-label="Search provider"
        className="ts-input ts-input-sm"
        placeholder="Search provider"
        value={q}
        onChange={(e)=>setQ(e.target.value)}
        onFocus={()=>setFocused(true)}
        // Do not auto-close on blur: on mobile, scrolling the portal dropdown can blur the input
        // and immediately collapse the dropdown, preventing scrolling.
        role="combobox"
        aria-expanded={!hasSelection && (focused || opts.length > 0 || loading)}
        aria-controls="provider-select-dropdown"
        aria-autocomplete="list"
        onKeyDown={(e)=>{
          // Previously Escape was the only key handled, so a long list could
          // only be worked through with the mouse.
          if (e.key === 'Escape') { setFocused(false); setActiveIndex(-1); return; }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocused(true);
            setActiveIndex((i) => (opts.length ? (i + 1) % opts.length : -1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (opts.length ? (i <= 0 ? opts.length - 1 : i - 1) : -1));
            return;
          }
          if (e.key === 'Enter' && activeIndex >= 0 && opts[activeIndex]) {
            e.preventDefault();
            onChange(opts[activeIndex]);
            setFocused(false);
            setActiveIndex(-1);
          }
        }}
      />

      {/* render dropdown in a body-level portal so it can't be overlapped by footer */}
      { !hasSelection && (focused || opts.length > 0 || loading) && rect && (() => {
        /**
         * The dropdown used to always open downwards at a fixed `max-h-[60vh]`.
         * With the provider field low in the viewport that put most of the list
         * — and the "add a new provider" action at its foot — below the fold
         * with nothing to scroll, so long lists were effectively unreachable.
         * It now opens whichever way has more room and sizes itself to the
         * space actually available, with the footer pinned outside the scroll
         * area so it is always one click away.
         */
        const GAP = 6;
        const MARGIN = 12;
        const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN;
        const spaceAbove = rect.top - GAP - MARGIN;
        const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
        const maxHeight = Math.max(160, Math.min(openUp ? spaceAbove : spaceBelow, 420));

        // The trigger is a narrow field inside the report form, so matching its
        // width exactly gave a cramped panel that truncated provider names and
        // websites. Widen to a readable minimum, then clamp to the viewport and
        // nudge back inside it so the panel can never hang off the right edge
        // on a phone.
        const width = Math.min(
          Math.max(rect.width, 288),
          Math.max(200, window.innerWidth - MARGIN * 2),
        );
        const left = Math.min(
          Math.max(MARGIN, rect.left),
          Math.max(MARGIN, window.innerWidth - width - MARGIN),
        );

        return (
        <Portal>
          <div
            ref={dropdownRef}
            id="provider-select-dropdown"
            data-test-id="provider-select-dropdown"
            role="listbox"
            aria-label="Provider results"
            style={{
              position: 'fixed',
              left,
              width,
              maxHeight,
              zIndex: 1200,
              pointerEvents: 'auto',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              ...(openUp
                ? { bottom: window.innerHeight - rect.top + GAP }
                : { top: rect.bottom + GAP }),
            } as React.CSSProperties}
            className="ts-portal"
          >
            {/* No explicit height: the portal is a flex column and the footer is
                flex:0 0 auto, so the list absorbs exactly the leftover space. */}
            <div className="ts-portal-list">
              {loading && <div className="ts-portal-note">Loading…</div>}
              {!loading && opts.length === 0 && (
                <div className="ts-portal-note">No providers found — you can add a new one below.</div>
              )}
              {opts.map((p, i)=> (
                <button
                  key={p._id || p.name}
                  type="button"
                  role="option"
                  aria-selected={i === activeIndex}
                  data-active={i === activeIndex}
                  onMouseEnter={()=>setActiveIndex(i)}
                  onClick={(e)=>{
                    e.preventDefault();
                    onChange(p);
                    setFocused(false);
                  }}
                  className="ts-portal-item"
                >
                  <b>
                    {p.name} {p.rank ? <span style={{ opacity: .55, fontWeight: 500 }}>#{p.rank}</span> : null}
                    {p.reportsCount && p.reportsCount >= 10 ? (
                      <span title="Many reports filed against this provider" style={{ marginLeft: 6 }}>⚠️</span>
                    ) : null}
                  </b>
                  <small>{p.website || (p.aliases && p.aliases.join(', ')) || '\u00A0'}</small>
                </button>
              ))}
            </div>
            <div className="ts-portal-foot">
              <button
                type="button"
                onMouseDown={(e)=>{
                  e.preventDefault();
                  // Signal to parent that the user wants to add a new provider with this name
                  onChange({ name: q || 'Other' } as Provider);
                }}
              >
                + Add new provider: “{q || 'Other'}”
              </button>
            </div>
          </div>
        </Portal>
        );
      })()}
    </div>
  );
}
